import type { Call, Device } from "@twilio/voice-sdk";
import { ConferenceOptions, Reservation } from "~/packages/taskrouter";
import type { VoiceCall } from "~/modules/VoiceCall";
import { VoiceCallImpl } from "~/modules/VoiceCall";
import { ErrorCode, ErrorSeverity, FlexSdkError } from "~/modules/error";
import { getLogger } from "~/modules/logger";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import {
    addCallContextParameter,
    getConferenceRecordingConfiguration,
    getDefaultCallerID,
    getDefaultQueueSid,
    getDefaultWorkflowSid,
    hasVoiceTaskWithStatus,
    isOutboundCallingEnabled,
    isOutboundCallPending,
    isWorkerOffline,
    sendTrackingEvent
} from "../../ActionUtils";
import { AccountConfigDataContainer } from "~/modules/config/AccountConfig/AccountConfigImpl/AccountConfigDataContainer/AccountConfigDataContainer";
import { VoiceControllerImpl } from "~/modules/voice/VoiceControllerImpl";
import { EVENTS } from "~/modules/analytics/Analytics";
import { AnalyticsImpl } from "~/modules/analytics/AnalyticsImpl";
import { Action } from "~/modules/actions/Action";
import { SessionImpl } from "~/modules/session/Session/SessionImpl";
import { ClientOptionsStore } from "~/modules/client/ClientOptions/ClientOptionsStore";
import { getConfigRegion } from "~/utils/regionUtil";




export interface StartOutboundCallOptions {
    fromNumber?: string;
    workflowSid?: string;
    taskQueueSid?: string;
    attributesForTaskCreation?: object;
    conferenceOptions?: Partial<ConferenceOptions>;
    voiceDevice?: Device;
}



















export class StartOutboundCall implements Action<Promise<VoiceCall>> {
    


    #toNumber: string;

    


    #options?: StartOutboundCallOptions;

    


    #defaultConferenceOptions: ConferenceOptions = {
        endConferenceOnCustomerExit: true,
        endConferenceOnExit: true
    };

    constructor(toNumber: string, options?: StartOutboundCallOptions) {
        this.#toNumber = toNumber;
        if (options) {
            this.#options = {
                ...options,
                conferenceOptions: {
                    ...this.#defaultConferenceOptions,
                    ...options.conferenceOptions
                }
            };
        } else {
            this.#options = {
                conferenceOptions: this.#defaultConferenceOptions
            };
        }
    }

    async run(ctx: {}): Promise<VoiceCall> {
        const context = ctx as ContextManager;

        const session = context.retrieveInstanceOf(ContextInstance.SessionImpl, SessionImpl);
        const flexSessionIdentifier = session.flexSessionIdentifier;

        const taskRouter = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        await taskRouter.init();
        const logger = getLogger(context)("StartOutboundCall");
        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);
        const accountConfig = context.retrieveInstanceOf(
            ContextInstance.AccountConfigDataContainer,
            AccountConfigDataContainer
        );
        const clientOptions = context.retrieveInstanceOf(ContextInstance.ClientOptionsStore, ClientOptionsStore);
        const voiceController = context.retrieveInstanceOf(ContextInstance.VoiceControllerImpl, VoiceControllerImpl);

        logger.debug(`startOutboundCall invoked to number: ${this.#toNumber}`);

        const worker = taskRouter.worker;
        const { attributesForTaskCreation, conferenceOptions, fromNumber, taskQueueSid, workflowSid } =
            this.#options || {};

        if (!worker) {
            const errorMsg = `startOutboundCall: worker is not initialized`;
            logger.error(errorMsg);
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        if (!this.#toNumber) {
            const errorMsg = `startOutboundCall: toNumber is a required parameter`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, { error: errorMsg });
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        const fromNumberParam = fromNumber || getDefaultCallerID(accountConfig.get().outboundCallFlows);
        if (!fromNumberParam) {
            const errorMsg = `startOutboundCall: fromNumber is required`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, { error: errorMsg });
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        const taskQueueSidParam = taskQueueSid || getDefaultQueueSid(accountConfig.get().outboundCallFlows);
        if (!taskQueueSidParam) {
            const errorMsg = `startOutboundCall: taskQueueSid is required`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, {
                error: errorMsg,
                fromNumber
            });
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        const workflowSidParam = workflowSid || getDefaultWorkflowSid(accountConfig.get().outboundCallFlows);
        if (!workflowSidParam) {
            const errorMsg = `startOutboundCall: workflowSid is required`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, {
                error: errorMsg,
                fromNumber,
                taskQueueSid
            });
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        const taskrouterOfflineActivitySid = accountConfig.get().taskrouterOfflineActivitySid;
        if (!taskrouterOfflineActivitySid) {
            const errorMsg = "startOutboundCall: taskrouterOfflineActivitySid is undefined";
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, {
                error: errorMsg,
                fromNumber,
                taskQueueSid,
                workflowSid
            });
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        if (isWorkerOffline(worker, taskrouterOfflineActivitySid)) {
            const errorMsg = `startOutboundCall: worker is offline, outbound call cancelled`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, {
                error: errorMsg,
                fromNumber,
                taskQueueSid,
                workflowSid
            });
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        if (!isOutboundCallingEnabled(accountConfig.get().outboundCallFlows)) {
            const errorMsg = `startOutboundCall: Outbound calling is disabled in Flex account configuration,
                                outbound call cancelled`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, {
                error: errorMsg,
                fromNumber,
                taskQueueSid,
                workflowSid
            });
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        
        if (isOutboundCallPending()) {
            const errorMsg = `startOutboundCall: Another outbound call is already pending, outbound call cancelled`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, {
                error: errorMsg,
                fromNumber,
                taskQueueSid,
                workflowSid
            });
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        if (hasVoiceTaskWithStatus(worker, "pending")) {
            const errorMsg = `startOutboundCall: Inbound call is pending, outbound call cancelled`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, {
                error: errorMsg,
                fromNumber,
                taskQueueSid,
                workflowSid
            });
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        if (hasVoiceTaskWithStatus(worker, "accepted")) {
            const errorMsg = `startOutboundCall: Another voice task is already in accepted status, outbound call cancelled`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, {
                error: errorMsg,
                fromNumber,
                taskQueueSid,
                workflowSid
            });
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        const voiceDevice = this.#options?.voiceDevice;
        await voiceController.init(voiceDevice);

        if (!voiceController.isAudioInputDeviceAvailable()) {
            const errorMsg = `startOutboundCall: no audio input device, outbound call cancelled`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, {
                error: errorMsg,
                fromNumber,
                taskQueueSid,
                workflowSid,
                customVoiceDevice: !!voiceDevice
            });
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        
        return new Promise(async (resolve, reject) => {
            let taskSid: string;
            let cleanup: (() => void) | undefined;

            const handleIncomingCall = (call: Call) => {
                logger.info(
                    "Incoming call event received in startOutboundCall",
                    call?.parameters?.From,
                    call?.parameters?.To
                );
                if (call?.parameters?.From === fromNumberParam) {
                    logger.debug("From phone numbers match");

                    cleanup?.();
                    const voiceCall = new VoiceCallImpl(context, call, voiceController.voiceDevice);

                    sendTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, {
                        taskSid,
                        fromNumber,
                        taskQueueSid,
                        workflowSid,
                        customVoiceDevice: !!voiceDevice,
                        callSid: call.parameters?.CallSid
                    });
                    resolve(voiceCall);
                } else {
                    logger.error(
                        "Incoming call is coming from a different number, not from the one given as an argument to startOutboundCall action"
                    );
                }
            };

            let handleReservationFailed: (reservation: Reservation) => void;

            const handleReservationCreated = async (reservation: Reservation) => {
                logger.info("A reservation has been created for the task", reservation?.task?.sid);
                if (reservation.task.sid === taskSid) {
                    try {
                        const to = addCallContextParameter(
                            `flex_session_identifier=${flexSessionIdentifier}`,
                            taskRouter.worker.attributes.contact_uri,
                            conferenceOptions?.to
                        );

                        const effectiveConferenceOptions = {
                            ...conferenceOptions,
                            to,
                            ...getConferenceRecordingConfiguration(
                                accountConfig.get(),
                                getConfigRegion(clientOptions),
                                taskSid,
                                conferenceOptions
                            )
                        };

                        const conferenceCreated = await reservation.conference(effectiveConferenceOptions);
                        logger.debug("Conference created", conferenceCreated);
                    } catch (error) {
                        const errorMsg = `startOutboundCall: Error creating conference: ${error.message}`;
                        logger.error(errorMsg, error);
                        sendTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, {
                            error: errorMsg,
                            taskSid,
                            fromNumber,
                            taskQueueSid,
                            workflowSid,
                            customVoiceDevice: !!voiceDevice
                        });
                        reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
                        cleanup?.();
                    } finally {
                        worker.off("reservationCreated", handleReservationCreated);
                        worker.off("reservationFailed", handleReservationFailed);
                    }
                }
            };

            handleReservationFailed = (reservation: Reservation) => {
                logger.error("Failed to create reservation for task:", reservation?.task?.sid);
                if (reservation.task.sid === taskSid) {
                    const errorMsg = `startOutboundCall: Error creating reservation`;
                    logger.error(errorMsg);
                    sendTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, {
                        error: errorMsg,
                        taskSid,
                        fromNumber,
                        taskQueueSid,
                        workflowSid,
                        customVoiceDevice: !!voiceDevice
                    });
                    reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
                    cleanup?.();
                }
            };

            const timeoutId = setTimeout(() => {
                const errorMsg = `Timeout: No incoming call event received within 30 seconds`;
                logger.error(errorMsg);
                sendTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, {
                    error: errorMsg,
                    taskSid,
                    fromNumber,
                    taskQueueSid,
                    workflowSid,
                    customVoiceDevice: !!voiceDevice
                });
                reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
                cleanup?.();
            }, 30000);

            cleanup = () => {
                clearTimeout(timeoutId);
                voiceController.unsubscribeFromIncomingCallEvent(handleIncomingCall);
                worker.off("reservationCreated", handleReservationCreated);
                worker.off("reservationFailed", handleReservationFailed);
            };

            voiceController.subscribeToIncomingCallEvent(handleIncomingCall);
            worker.on("reservationCreated", handleReservationCreated);
            worker.on("reservationFailed", handleReservationFailed);

            try {
                const outboundAttributes = {
                    ...attributesForTaskCreation,
                    direction: "outbound",
                    flex_session_identifier_task_created: flexSessionIdentifier
                };
                const taskOptions = {
                    taskChannelUniqueName: "voice",
                    attributes: outboundAttributes
                };

                taskSid = await worker.createTask(
                    this.#toNumber,
                    fromNumberParam,
                    workflowSidParam,
                    taskQueueSidParam,
                    taskOptions
                );
            } catch (error) {
                cleanup();
                const errorMsg = `startOutboundCall: Failed to create task: ${error.message}`;
                sendTrackingEvent(EVENTS.StartOutboundCallCompleted, analytics, worker, {
                    error: errorMsg,
                    fromNumber,
                    taskQueueSid,
                    workflowSid,
                    customVoiceDevice: !!voiceDevice
                });
                logger.error(errorMsg, error);
                reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
            }
        });
    }
}
